import { ViewHook } from "phoenix_live_view";

import type { EChartsCoreOption } from "echarts/core";

import type { ChartHandle } from "../charts/echarts";
import { FONT_FAMILY } from "../constants";
import {
  whenVisible,
  afterInitialResizeNotification,
  type VisibilityWait,
} from "../utils/charts";

// Reusable chart for the fund flows report deck. The full echarts option (bar/line/pie) is
// built server-side and passed as a JSON `data-option`, so one hook covers every slide.
class DeckChart extends ViewHook {
  private chartHandle?: ChartHandle;
  private isDestroyed?: boolean;
  private visibilityWait?: VisibilityWait;

  // LiveView ignores mounted()'s return value, so the promise is safe to return.
  // oxlint-disable-next-line typescript/no-misused-promises
  async mounted() {
    const { option: optionStr } = this.el.dataset;

    if (!optionStr) {
      console.error("DeckChart: missing option");
      return;
    }

    const animateWhenVisible = "animateWhenVisible" in this.el.dataset;

    if (animateWhenVisible) {
      this.visibilityWait = whenVisible(this.el, 1);
      await this.visibilityWait.visible;
      this.visibilityWait = undefined;
    }

    // Imported lazily so pages without charts never download echarts.
    const { createChart } = await import("../charts/echarts");

    if (this.isDestroyed) {
      return;
    }

    const parsed = JSON.parse(optionStr) as { textStyle?: Record<string, unknown> };

    // Font isn't serializable from Elixir — inject it here while keeping any option-level overrides.
    const option = {
      ...parsed,
      textStyle: { fontFamily: FONT_FAMILY, ...parsed.textStyle },
    };

    this.chartHandle = createChart(this.el);

    if (animateWhenVisible) {
      await afterInitialResizeNotification();
    }

    this.chartHandle.chart.setOption(option as EChartsCoreOption);
  }

  destroyed() {
    this.isDestroyed = true;

    this.visibilityWait?.cancel();
    this.visibilityWait = undefined;

    this.chartHandle?.destroy();
    this.chartHandle = undefined;
  }
}

export default DeckChart;
